/**
 * E37 · the diary card's CATALOGUE — constants the phone, the portal and the template seeds share (cc-assessments, chapter
 * "Check-in — Diary card (portal + mobile, E37)"). Names are i18n content (`diaryCard.row.*`, `diaryCard.skill.*`,
 * `diaryCard.module.*`, `diaryCard.emotion.*`); only ids live here.
 *
 * THE SKILLS are Linehan's four modules. The list is PROVISIONAL until Dmitri reviews the skill names (Q-emo-7) — a content
 * change, not a model change.
 */
export const DIARY_CARD_SKILL_MODULES = [
  'mindfulness',
  'distressTolerance',
  'emotionRegulation',
  'interpersonalEffectiveness',
] as const;
export type DiaryCardSkillModule = (typeof DIARY_CARD_SKILL_MODULES)[number];

const skills = (module: DiaryCardSkillModule, ids: string[]) =>
  ids.map((skillId) => ({ skillId, module }));

export const DIARY_CARD_SKILLS: ReadonlyArray<{
  skillId: string;
  module: DiaryCardSkillModule;
}> = [
  ...skills('mindfulness', [
    'wiseMind',
    'observe',
    'describe',
    'participate',
    'nonjudgmental',
    'oneMindful',
    'effective',
  ]),
  ...skills('distressTolerance', [
    'tipp',
    'stop',
    'proCons',
    'distract',
    'selfSoothe',
    'improve',
    'radicalAcceptance',
    'turningTheMind',
    'willingness',
  ]),
  ...skills('emotionRegulation', [
    'checkTheFacts',
    'oppositeAction',
    'problemSolving',
    'accumulatePositives',
    'buildMastery',
    'copeAhead',
    'please',
    'mindfulOfEmotions',
  ]),
  ...skills('interpersonalEffectiveness', [
    'dearMan',
    'give',
    'fast',
    'validation',
    'findingBalance',
    'walkTheMiddlePath',
  ]),
];

/** The UW / NIMH card's three emotion rows (rated 0–5); any id of the app's emotion catalogue may be added. */
export const DIARY_CARD_UW_EMOTIONS = [
  'emotionalMisery',
  'physicalMisery',
  'joy',
] as const;

/** Sadness · anger · shame — the groups of the app's emotion catalogue the DBT standard card adds to the UW rows. */
export const DIARY_CARD_STANDARD_EMOTION_GROUPS = [1, 3, 7] as const;

/**
 * A row of the catalogue is ids, a kind, a scale and flags — NO words. Its name is i18n content, read by id from
 * `diaryCard.row.<targetId>` (EN + generated RU) wherever it is shown or seeded.
 */
export type DiaryCardTargetShape = {
  targetId: string;
  kind: 'urge' | 'scale' | 'action' | 'count' | 'text';
  max?: number;
  countable?: boolean;
  standard?: boolean;
};

/** The NIMH / UW card's target rows — the catalogue rows ("standard": the same id and scale is the same row on every card). Names: i18n by id. */
export const DIARY_CARD_STANDARD_ROWS: ReadonlyArray<DiaryCardTargetShape> = [
  { targetId: 't-suicide-urge', kind: 'urge', max: 5, standard: true },
  { targetId: 't-selfharm-urge', kind: 'urge', max: 5, standard: true },
  { targetId: 't-drugs-urge', kind: 'urge', max: 5, standard: true },
  { targetId: 't-selfharm', kind: 'action', standard: true },
  { targetId: 't-binge', kind: 'action', countable: true, standard: true },
];
